import { ModalDialog } from '@components/ModalDialog';
import type { CatalogUerBaseCostData } from '@gql/graphql';
import {
    type CatalogRatioGroupData,
    type CatalogRatioShortData,
    DismantlingCoefficientTypeEnum,
    RatioTypeEnum,
} from '@gql/graphql';
import { Button, cnMixSpace, Steps } from '@uikit';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AddRatiosModalToolbar } from '../AddRatiosModal/components';
import { useGroupsData } from '../CreateOrEditRatioModal/hooks';

import { SelectBaseCostsStep, SelectRatiosStep } from './components';
import { DEFAULT_ADD_RATIO_STEPS } from './constants';
import { changeTableFilterHandler, getItemCompleted, getItemDisabledDefault } from './helpers';
import { useMassApplicationBaseCostData, useMassApplicationRatioData, useMassApplyRatios } from './hooks';
import type { StepsContent, TRatioTypeSelectItem } from './types';

import Styles from './massRatioApplication.module.css';

type TMassRatioApplicationProps = {
    closeModal: () => void;
};

const modalTitle = 'Добавление коэффициента к расценке';

export const MassRatioApplication = ({ closeModal }: TMassRatioApplicationProps) => {
    const [activeStep, setActiveStep] = useState(DEFAULT_ADD_RATIO_STEPS[0]);
    const [selectedRatioType, setSelectedRatioType] = useState<TRatioTypeSelectItem | null>(null);
    const [selectedRatioGroup, setSelectedRatioGroup] = useState<CatalogRatioGroupData | null>(null);
    const [checkedRatios, setCheckedRatios] = useState<Record<string, CatalogRatioShortData | null>>({});
    const [checkedBaseCosts, setCheckedBaseCosts] = useState<Record<string, CatalogUerBaseCostData | null>>({});
    const [ratiosTabFilters, setRatiosTabFilters] = useState({});
    const [baseCostsTabFilters, setBaseCostsTabFilters] = useState({});

    const { id: catalogUniqueId } = useParams();

    const stepItemLabel = useCallback(
        (item: (typeof DEFAULT_ADD_RATIO_STEPS)[number]) => {
            if (
                item.stepId === DEFAULT_ADD_RATIO_STEPS[0].stepId &&
                Object.values(checkedRatios).filter(Boolean)?.length
            ) {
                return (
                    <>
                        {item.label}{' '}
                        <span className="Steps-Item-label">{Object.values(checkedRatios).filter(Boolean)?.length}</span>
                    </>
                ) as unknown as string;
            }

            return item?.label;
        },
        [checkedRatios],
    );

    const ratioGroupIds = useMemo(
        () =>
            R.values(checkedRatios).reduce((acc: string[], ratioData) => {
                if (ratioData?.catalogRatioGroupId && !acc.includes(String(ratioData?.catalogRatioGroupId))) {
                    return [...acc, String(ratioData?.catalogRatioGroupId)];
                }

                return acc;
            }, []),
        [checkedRatios],
    );

    const isConstructionWorksGroupSelected = useMemo(
        () =>
            selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.ConstructionWorks &&
            !selectedRatioGroup?.parentId,
        [selectedRatioGroup?.groupType, selectedRatioGroup?.parentId],
    );

    const {
        data: ratiosData,
        responseRatioType,
        responseGroupId,
        subgroupsData,
        subGroupsNames,
    } = useMassApplicationRatioData({
        selectedRatioGroup,
        ratioType: selectedRatioType?.id,
        filters: ratiosTabFilters,
        isConstructionWorksGroupSelected,
    });

    const {
        data: baseCostsData,
        changeOrderHandler: changeBaseCostsOrderHandler,
        reset: baseCostDataReset,
        dataSource: baseCostDataSource,
    } = useMassApplicationBaseCostData({
        id: catalogUniqueId,
        filterValues: baseCostsTabFilters,
    });

    const onChangeStep = useCallback(
        ({ value }: { value: (typeof DEFAULT_ADD_RATIO_STEPS)[number] }) => {
            setActiveStep((prevState) => {
                if (value?.stepId !== prevState?.stepId) {
                    setCheckedBaseCosts({});
                }
                baseCostDataReset();

                return value;
            });
        },
        [baseCostDataReset],
    );

    const { massApplyHandler } = useMassApplyRatios({
        onSuccessAction: () => {
            closeModal();
        },
    });

    useEffect(() => {
        if (baseCostsData.length) {
            if (
                selectedRatioType?.id === RatioTypeEnum.ConstraintCoef ||
                selectedRatioGroup?.groupType === DismantlingCoefficientTypeEnum.EquipmentInstallation
            ) {
                const checkedItems = baseCostsData.reduce(
                    (acc, baseCost) => ({
                        ...acc,
                        [baseCost.id]: true,
                    }),
                    {},
                );

                setCheckedBaseCosts((ids) => ({ ...ids, ...checkedItems }));
            }
        }
    }, [baseCostsData, selectedRatioGroup?.groupType, selectedRatioType?.id]);

    const { groupsData } = useGroupsData({
        ratioTypeId: selectedRatioType?.id,
        groupsEffect: !!selectedRatioType?.id && selectedRatioType.id !== RatioTypeEnum.TechPartCoef,
        dismantlingCoef: DismantlingCoefficientTypeEnum.ConstructionWorks,
        parentId: null,
    });

    const groupsItems = useMemo(
        () => groupsData?.filter((group) => !group.parentId).map((group) => ({ ...group, label: group.name })),
        [groupsData],
    );

    const handleModalConfirm = useCallback(() => {
        const checkedRatiosArray = Object.keys(checkedRatios ?? {})
            .filter((ratioId) => checkedRatios[ratioId])
            .map(Number);
        const checkedBaseCostsArray = Object.keys(checkedBaseCosts ?? {}).map(Number);
        const checkedRatioGroupsArray = ratioGroupIds.map(Number);

        massApplyHandler({
            ratiosIds: selectedRatioType?.id === RatioTypeEnum.TechPartCoef ? checkedRatiosArray : [],
            uerBaseCostIds: checkedBaseCostsArray,
            ratioGroupIds: checkedRatioGroupsArray,
        });
    }, [checkedBaseCosts, checkedRatios, massApplyHandler, ratioGroupIds, selectedRatioType?.id]);

    const isNextStepDisabled = useMemo(
        () => !Object.values(checkedRatios ?? {})?.some((checked) => checked),
        [checkedRatios],
    );

    const onStepClickCreator = useCallback(
        (step: (typeof DEFAULT_ADD_RATIO_STEPS)[number]) => () => {
            onChangeStep({ value: step });
        },
        [onChangeStep],
    );

    const firstStepContent = useCallback(
        (onStepClick: (step: (typeof DEFAULT_ADD_RATIO_STEPS)[number]) => () => void) => (
            <Button
                size="s"
                label="Далее"
                onClick={onStepClick(DEFAULT_ADD_RATIO_STEPS[1])}
                disabled={isNextStepDisabled}
            />
        ),
        [isNextStepDisabled],
    );

    const lastStepContent = useCallback(
        (onStepClick: (step: (typeof DEFAULT_ADD_RATIO_STEPS)[number]) => () => void) => (
            <div>
                <Button
                    size="s"
                    label="Добавить"
                    onClick={handleModalConfirm}
                    disabled={!R.keys(checkedBaseCosts).length}
                    className={cnMixSpace({
                        mR: 'm',
                    })}
                />
                <Button size="s" view="secondary" label="Назад" onClick={onStepClick(DEFAULT_ADD_RATIO_STEPS[0])} />
            </div>
        ),
        [checkedBaseCosts, handleModalConfirm],
    );

    const stepsFooterContent = useMemo<StepsContent>(
        () => ({
            0: firstStepContent,
            1: lastStepContent,
        }),
        [lastStepContent, firstStepContent],
    );

    const handleChangeRatioType = useCallback((value: TRatioTypeSelectItem | null) => {
        setCheckedRatios({});
        setSelectedRatioGroup(null);
        setSelectedRatioType(value);
    }, []);

    const handleChangeRatioGroup = useCallback((value: CatalogRatioGroupData | null) => {
        setCheckedRatios({});
        setSelectedRatioGroup(value);
    }, []);

    const tableUniqueId = useMemo(
        () => `massRatioApplication_${activeStep.stepId}_${selectedRatioType?.id}_${selectedRatioGroup?.id}`,
        [activeStep.stepId, selectedRatioGroup?.id, selectedRatioType?.id],
    );

    const selectedSubgroup = useMemo(() => {
        if (
            !selectedRatioGroup ||
            selectedRatioGroup.groupType !== DismantlingCoefficientTypeEnum.ConstructionWorks ||
            groupsItems?.find((group) => String(group.id) === ratioGroupIds[0])
        ) {
            return null;
        }

        return subgroupsData.find((subgroup) => String(subgroup.id) === ratioGroupIds[0]) ?? null;
    }, [groupsItems, ratioGroupIds, selectedRatioGroup, subgroupsData]);

    const contentByStep = useMemo(
        () => ({
            [DEFAULT_ADD_RATIO_STEPS[0].stepId]: (
                <SelectRatiosStep
                    selectedRatioType={selectedRatioType}
                    selectedRatioGroup={selectedRatioGroup}
                    checkedRatios={checkedRatios}
                    setCheckedRatios={setCheckedRatios}
                    tableUniqueId={tableUniqueId}
                    changeFilterHandler={changeTableFilterHandler(setRatiosTabFilters)}
                    responseRatioType={responseRatioType}
                    responseRatioGroupId={responseGroupId}
                    ratiosData={ratiosData}
                    subGroupsNames={subGroupsNames}
                    ratioGroupIds={ratioGroupIds}
                />
            ),
            [DEFAULT_ADD_RATIO_STEPS[1].stepId]: (
                <SelectBaseCostsStep
                    selectedRatioType={selectedRatioType}
                    selectedRatioGroup={responseGroupId ? selectedRatioGroup : null}
                    checkedRatios={checkedRatios}
                    tableUniqueId={tableUniqueId}
                    changeFilterHandler={changeTableFilterHandler(setBaseCostsTabFilters)}
                    responseRatioType={responseRatioType}
                    baseCostsData={baseCostsData}
                    checkedBaseCosts={checkedBaseCosts}
                    setCheckedBaseCosts={setCheckedBaseCosts}
                    changeOrderHandler={changeBaseCostsOrderHandler}
                    ratioGroupIds={ratioGroupIds}
                    selectedSubgroup={selectedSubgroup}
                    baseCostDataSource={baseCostDataSource}
                />
            ),
        }),
        [
            baseCostDataSource,
            baseCostsData,
            changeBaseCostsOrderHandler,
            checkedBaseCosts,
            checkedRatios,
            ratioGroupIds,
            ratiosData,
            responseGroupId,
            responseRatioType,
            selectedRatioGroup,
            selectedRatioType,
            selectedSubgroup,
            subGroupsNames,
            tableUniqueId,
        ],
    );

    return (
        <ModalDialog
            title={modalTitle}
            isOpen
            hasOverlay
            onClose={closeModal}
            withTitleCloseButton
            className="modalDialogAddCoefficient"
            withBottomButtons={false}
            isOnClickOutsideDisable
        >
            <Steps
                size="s"
                items={DEFAULT_ADD_RATIO_STEPS}
                getItemLabel={stepItemLabel}
                value={activeStep}
                onChange={onChangeStep}
                className={Styles.steps}
                getItemDisabled={getItemDisabledDefault(isNextStepDisabled)}
                getItemCompleted={getItemCompleted(activeStep)}
            />
            {activeStep?.stepId === 0 && (
                <AddRatiosModalToolbar
                    ratioGroups={groupsItems}
                    selectedRatioType={selectedRatioType?.id ?? null}
                    setSelectedRatioType={handleChangeRatioType}
                    selectedCatalogRatioGroup={selectedRatioGroup}
                    onSelectCatalogRatioGroupId={handleChangeRatioGroup}
                    canSearch={false}
                    ratioGroupSelectDisabled={!selectedRatioType?.id}
                    availableRatioTypes={[RatioTypeEnum.TechPartCoef, RatioTypeEnum.DismantlingCoef]}
                />
            )}
            {contentByStep[activeStep.stepId]}
            <div className={Styles.footerBlock}>{stepsFooterContent?.[activeStep?.stepId]?.(onStepClickCreator)}</div>
        </ModalDialog>
    );
};

MassRatioApplication.displayName = 'MassRatioApplication';
